import { DragSample, releaseVelocity } from "../render/cameraMotion";

/**
 * Touch-only gesture source on Pointer Events (ADR 0004). A mouse acts as a single touch for desktop
 * development. Pointers that start on UI (anything outside the canvas) never reach the world.
 */
export interface GestureSettings {
  tapMaxMovePx: number;
  tapMaxMs: number;
  dragDeadZonePx: number;
  longPressMs: number;
  /** Change in finger separation (px) before a two-finger touch can count as a pinch. */
  pinchDeadZonePx: number;
  /**
   * A pinch locks in early only when the squeeze overwhelms the twist arc (squeeze ≥ this × arc). A pinch is nearly all
   * squeeze, while a twist whose separation wobbles ±15 % can squeeze 3× its arc in its first few degrees…
   */
  pinchDominance: number;
  /**
   * …or once the separation has changed by this fraction of where it started whatever the twist did: a turn made with
   * real fingers wobbles the separation by ±10–15 %, a pinch soon goes past that.
   */
  pinchCommitFrac: number;
  /**
   * A turn is measured as the fingertips' twist ARC in px (angle × separation), so it compares like with like against
   * the pinch's change in separation. The arc must reach this before a touch can count as a turn…
   */
  rotateStartPx: number;
  /** …the twist must also reach this angle, so fingers spread wide apart cannot start a turn with a slight skew… */
  rotateMinDeg: number;
  /** …and the arc must beat the separation change by this factor: a turn has to be clearly a twist, not a squeeze. */
  rotateDominance: number;
  /**
   * Desktop stand-ins for a pinch (the mouse is only a touch stand-in): a wheel notch zooms by e^(px × this), a
   * trackpad pinch (a wheel event with ctrlKey, in much smaller steps) by e^(px × `trackpadZoomPerPx`).
   */
  wheelZoomPerPx: number;
  trackpadZoomPerPx: number;
}

export const defaultGestureSettings: GestureSettings = {
  tapMaxMovePx: 18, tapMaxMs: 350, dragDeadZonePx: 12, longPressMs: 600,
  pinchDeadZonePx: 6, pinchDominance: 4, pinchCommitFrac: 0.2,
  rotateStartPx: 24, rotateMinDeg: 8, rotateDominance: 1.5,
  wheelZoomPerPx: 0.002, trackpadZoomPerPx: 0.01,
};

/** A wheel event's delta in px (line and page modes are scaled to about what a notch scrolls). */
const WHEEL_LINE_PX = 16, WHEEL_PAGE_PX = 400;

export interface GestureHandlers {
  tap?(x: number, y: number): void;
  dragBegin?(x: number, y: number): void;
  dragMove?(x: number, y: number, dx: number, dy: number): void;
  /** `vx`, `vy`: the finger's speed as it lifted (px/s; 0 for a finger that stopped first), for the pan's momentum. */
  dragEnd?(x: number, y: number, vx: number, vy: number): void;
  /** Every move of a pinch once it has locked in, as the change in finger separation since the last one (no steps). */
  pinch?(scale: number, cx: number, cy: number): void;
  rotate?(degrees: number): void;
  /**
   * A two-finger touch began or ended: any partial turn is dropped, so a turn is always measured within one touch and
   * small twists across several touches never add up to a surprise 90°.
   */
  rotateEnd?(): void;
  longPress?(x: number, y: number): void;
}

interface P { x: number; y: number }

export class GestureSource {
  private readonly pointers = new Map<number, P>();
  private primaryId = -1;
  private start: P = { x: 0, y: 0 };
  private last: P = { x: 0, y: 0 };
  private startTime = 0;
  private dragging = false;
  private longPressed = false;
  private twoFinger = false;
  private lastDist = 0;
  private lastAngle = 0;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  /** The drag's recent finger positions (oldest first, the last ~FlingWindowMs), for the release velocity. */
  private readonly trail: DragSample[] = [];
  /** How many samples the trail keeps: a window's worth at 120 Hz and a margin. */
  static readonly TrailSamples = 16;

  constructor(private readonly target: HTMLElement, private readonly handlers: GestureHandlers, private readonly settings = defaultGestureSettings) {
    target.style.touchAction = "none";
    target.addEventListener("pointerdown", this.onDown);
    target.addEventListener("pointermove", this.onMove);
    target.addEventListener("pointerup", this.onUp);
    target.addEventListener("pointercancel", this.onUp);
    target.addEventListener("contextmenu", this.onContextMenu);
    target.addEventListener("wheel", this.onWheel, { passive: false });
  }

  dispose(): void {
    this.target.removeEventListener("pointerdown", this.onDown);
    this.target.removeEventListener("pointermove", this.onMove);
    this.target.removeEventListener("pointerup", this.onUp);
    this.target.removeEventListener("pointercancel", this.onUp);
    this.target.removeEventListener("contextmenu", this.onContextMenu);
    this.target.removeEventListener("wheel", this.onWheel);
  }

  /** The desktop's pinch: the mouse wheel or a trackpad pinch zooms about the pointer, as two fingers would. */
  private readonly onWheel = (e: WheelEvent): void => {
    if (!this.handlers.pinch) return;
    e.preventDefault();
    const px = e.deltaY * (e.deltaMode === 1 ? WHEEL_LINE_PX : e.deltaMode === 2 ? WHEEL_PAGE_PX : 1);
    if (px === 0) return;
    const k = e.ctrlKey ? this.settings.trackpadZoomPerPx : this.settings.wheelZoomPerPx;
    this.handlers.pinch(Math.exp(-px * k), e.clientX, e.clientY);
  };

  /**
   * A named handler so dispose can remove it: the target is the game canvas, which outlives every level, and an
   * anonymous listener there kept this source — and through `handlers` the whole LevelController and its scene —
   * alive after each level (RC1 FX-C: the per-level heap leak).
   */
  private readonly onContextMenu = (e: Event): void => e.preventDefault();

  private onDown = (e: PointerEvent): void => {
    if (import.meta.env.DEV) console.debug(`[gesture] down id=${e.pointerId} type=${e.pointerType} button=${e.button}`);
    if (e.pointerType === "mouse" && e.button !== 0) return;
    try { this.target.setPointerCapture(e.pointerId); } catch { /* synthetic or already-released pointer */ }
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.pointers.size === 1) {
      this.primaryId = e.pointerId;
      this.start = this.last = { x: e.clientX, y: e.clientY };
      this.startTime = performance.now();
      this.dragging = false;
      this.longPressed = false;
      this.clearLongPress();
      this.longPressTimer = setTimeout(() => {
        if (!this.dragging && !this.twoFinger && this.pointers.has(this.primaryId)) { this.longPressed = true; this.handlers.longPress?.(this.last.x, this.last.y); }
      }, this.settings.longPressMs);
    } else if (this.pointers.size === 2) {
      this.beginTwoFinger();
    } else if (this.twoFinger) {
      this.rebase();
    }
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this.twoFinger) { this.updateTwoFinger(); return; }
    if (e.pointerId !== this.primaryId) return;
    const p = { x: e.clientX, y: e.clientY };
    if (!this.dragging && Math.hypot(p.x - this.start.x, p.y - this.start.y) > this.settings.dragDeadZonePx) {
      this.dragging = true;
      this.clearLongPress();
      this.trail.length = 0;
      this.handlers.dragBegin?.(this.start.x, this.start.y);
      this.last = this.start;
    }
    if (this.dragging) {
      this.handlers.dragMove?.(p.x, p.y, p.x - this.last.x, p.y - this.last.y);
      this.last = p;
      this.trail.push({ x: p.x, y: p.y, t: eventTime(e) });
      if (this.trail.length > GestureSource.TrailSamples) this.trail.shift();
    }
  };

  private onUp = (e: PointerEvent): void => {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.delete(e.pointerId);
    if (this.twoFinger) {
      if (this.pointers.size < 2) { this.twoFinger = false; this.primaryId = -1; this.handlers.rotateEnd?.(); this.twoMode = "none"; }
      else this.rebase();                    // a third finger lifted: the pair is now a different two pointers
      return;
    }
    if (e.pointerId !== this.primaryId) return;
    this.clearLongPress();
    this.primaryId = -1;
    const p = { x: e.clientX, y: e.clientY };
    if (this.dragging) {
      // a cancelled touch (the system took it) throws nothing
      const v = e.type === "pointerup" ? releaseVelocity(this.trail, eventTime(e)) : { vx: 0, vy: 0 };
      this.handlers.dragEnd?.(p.x, p.y, v.vx, v.vy);
    }
    else if (!this.longPressed && e.type === "pointerup" && Math.hypot(p.x - this.start.x, p.y - this.start.y) <= this.settings.tapMaxMovePx && performance.now() - this.startTime <= this.settings.tapMaxMs) {
      this.handlers.tap?.(p.x, p.y);
    } else if (import.meta.env.DEV) {
      console.debug(`[gesture] up without tap: dragging=${this.dragging} longPressed=${this.longPressed} type=${e.type} moved=${Math.hypot(p.x - this.start.x, p.y - this.start.y).toFixed(1)} ms=${(performance.now() - this.startTime).toFixed(0)}`);
    }
  };

  private beginTwoFinger(): void {
    this.twoFinger = true;
    this.twoMode = "none";
    this.clearLongPress();
    if (this.dragging) { this.dragging = false; this.handlers.dragEnd?.(this.last.x, this.last.y, 0, 0); }
    this.handlers.rotateEnd?.();             // a fresh turn: nothing left over from the rotate buttons or a last touch
    this.rebase();
  }

  /**
   * Measures the two-finger gesture from the current pair again. Called when it starts, and when a third finger lands
   * or lifts: the pair can then be two different pointers, and carrying the old pair's distance and angle over made
   * the view jump. An undecided gesture restarts its pinch-or-turn decision from here.
   */
  private rebase(): void {
    const [a, b] = [...this.pointers.values()];
    this.lastDist = this.startDist = Math.hypot(b.x - a.x, b.y - a.y);
    this.lastAngle = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
    this.twistSum = 0;
  }

  /**
   * A two-finger gesture is EITHER a pinch or a turn, and a turn must be clearly intended. Rotation used to start at
   * half a degree of finger twist, so every pinch spun the map (Kevin's play-test); then a 14° twist dead zone raced a
   * 6 px pinch dead zone and a turn was nearly impossible to start. Both are now measured in px of fingertip travel
   * since the touch began — the twist as its arc (angle × separation), the pinch as the change in separation.
   *
   * A turn locks in when the arc is long enough, the angle big enough AND the arc clearly dominates the squeeze. A
   * pinch locks in when the squeeze passes its dead zone AND clearly dominates the arc, or when the separation has
   * changed by more than a real twist's wobble. Anything in between waits. The pinch used to lock as soon as the
   * squeeze passed 6 px without the twist dominating (2026-09-22): a real twist wobbles the fingers' separation by
   * ±10–15 %, which is 6 px within the first 2–3° of the turn, so on the phone every twist became a pinch.
   */
  private twoMode: "none" | "pinch" | "rotate" = "none";
  private twistSum = 0;
  private startDist = 0;
  private updateTwoFinger(): void {
    const [a, b] = [...this.pointers.values()];
    const dist = Math.hypot(b.x - a.x, b.y - a.y);
    const angle = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
    let d = angle - this.lastAngle;
    while (d > 180) d -= 360;
    while (d < -180) d += 360;
    if (this.twoMode === "none") {
      this.twistSum += d;
      this.lastAngle = angle;
      const s = this.settings;
      const squeezePx = Math.abs(dist - this.startDist);
      const arcPx = Math.abs(this.twistSum) * Math.PI / 180 * (dist + this.startDist) / 2;
      if (arcPx >= s.rotateStartPx && Math.abs(this.twistSum) >= s.rotateMinDeg && arcPx >= s.rotateDominance * squeezePx) {
        this.twoMode = "rotate"; this.handlers.rotate?.(this.twistSum); this.twistSum = 0; return;
      }
      const squeezeDominates = squeezePx >= s.pinchDominance * arcPx;
      const pastWobble = squeezePx > s.pinchCommitFrac * this.startDist;
      if (squeezePx > s.pinchDeadZonePx && this.lastDist > 1 && (squeezeDominates || pastWobble)) this.twoMode = "pinch";
      else return;
    }
    if (this.twoMode === "pinch") {
      // Every move once locked in, not in 6 px steps (QoL C1): the dead zone only decides whether this is a pinch.
      // The camera eases to the fingers' zoom about the point between them (render/cameraMotion.ts).
      if (dist !== this.lastDist && this.lastDist > 1) {
        this.handlers.pinch?.(dist / this.lastDist, (a.x + b.x) / 2, (a.y + b.y) / 2);
        this.lastDist = dist;
      }
      return;
    }
    if (Math.abs(d) > 0.5) { this.handlers.rotate?.(d); this.lastAngle = angle; }
  }

  private clearLongPress(): void {
    if (this.longPressTimer !== null) { clearTimeout(this.longPressTimer); this.longPressTimer = null; }
  }
}

/** An event's time in ms on the performance clock (a synthetic event without one reads the clock now). */
function eventTime(e: Event): number { return e.timeStamp > 0 ? e.timeStamp : performance.now(); }
